import { useCallback, useId, useState } from 'react'
import styled from 'styled-components'
import { Copy, Tooltip, t } from '@stokr/components-library'
import { ExternalLinkIcon } from 'components/ui/icons'
import { SectionSkeleton } from 'components/sto/Section'
import { TabHighlightBase, TabPanel, tabColorTransition } from 'components/sto/Tabs'
import { pickTradeVenue, useTradePanel, type TradeVenueSource } from 'hooks/sto/useTrading'
import { stoColor, stoRadius, stoSpace } from 'constants/stoTheme'
import { Notice } from './Notice'
import { TradeRiskModal } from './TradeRiskModal'
import { Disclaimer, LockedTrigger, TradeAction } from './KeyActions.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const VenueTrack = styled.div`
  position: relative;
  display: flex;
  gap: ${stoSpace[1]};
  width: 100%;
  padding: ${stoSpace[1]};
  background: ${stoColor.surfaceAlt};
  border-radius: ${stoRadius.lg};
`

/** The raised white pill, sliding between venues. */
const VenueHighlight = styled(TabHighlightBase)`
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.sm};
  box-shadow:
    0px 2px 2px rgba(0, 0, 0, 0.04),
    0px 4px 4px rgba(0, 0, 0, 0.08);
`

const VenueTab = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  padding: ${stoSpace[2]} ${stoSpace[6]};
  border: none;
  border-radius: ${stoRadius.sm};
  cursor: pointer;
  background: transparent;
  white-space: nowrap;
`

const VenueName = styled.span<{ $active: boolean }>`
  ${tabColorTransition};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${({ $active }) => ($active ? stoColor.textPrimary : stoColor.disabledText)};
`

const VenueSubtitle = styled.span<{ $active: boolean }>`
  ${tabColorTransition};
  font-size: 10px;
  line-height: normal;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? stoColor.textSecondary : stoColor.disabledText)};
`

/** The venue's content, spaced like the panel around it. */
const VenueBody = styled(TabPanel)`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[6]};
`

/**
 * Trade tab (Figma 3474:44685 / 3474:44754 / 3474:45271 / 3306:20202 / 2897:21321): the
 * product's venues, SideSwap first. Bitfinex runs its own onboarding; SideSwap opens once
 * the visitor is eligible for the secondary market on STOKR. Not tradable: one notice.
 */
export const TradePanel = ({
  pickedSource,
  onPickSource,
}: {
  /** Held by KeyActions: the eligibility footer under the card depends on the venue. */
  pickedSource?: TradeVenueSource
  onPickSource: (source: TradeVenueSource) => void
}) => {
  const { isPending, venues } = useTradePanel()
  const [riskModalOpen, setRiskModalOpen] = useState(false)
  const closeRiskModal = useCallback(() => setRiskModalOpen(false), [])
  const panelId = useId()
  const trackSto = useStoTrack()

  if (isPending) return <SectionSkeleton rows={3} rowHeight={48} />
  if (venues.length === 0) {
    return <Notice variant="warning" titleKey="stoPage.keyActions.trade.unavailableTitle" />
  }

  const active = pickTradeVenue(venues, pickedSource) ?? venues[0]

  return (
    <>
      <VenueTrack role="tablist" aria-label={t('stoPage.keyActions.trade.venueLabel')}>
        <VenueHighlight $index={venues.indexOf(active)} $count={venues.length} $gap={4} $inset={4} aria-hidden="true" />
        {venues.map((venue) => {
          const isActive = venue === active
          return (
            <VenueTab
              key={venue.source}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              data-cy={`sto-trade-venue-${venue.source}`}
              onClick={() => {
                if (!isActive) trackSto('sto_trade_venue_selected', { venue: venue.source })
                onPickSource(venue.source)
              }}>
              <VenueName $active={isActive}>{venue.name}</VenueName>
              <VenueSubtitle $active={isActive}>
                <Copy id={venue.subtitleKey} />
              </VenueSubtitle>
            </VenueTab>
          )
        })}
      </VenueTrack>

      <VenueBody activeKey={active.source} id={panelId}>
        {active.isChecking ? (
          <SectionSkeleton rows={1} rowHeight={52} />
        ) : (
          <Tooltip placement="top" content={active.lockedReasonKey && t(active.lockedReasonKey)} maxWidth={280}>
            {/* Focusable only while locked, so the reason is reachable by keyboard too. `black`: the
                library outlined button. Bitfinex asks for the risk acknowledgement first. */}
            <LockedTrigger
              tabIndex={active.lockedReasonKey ? 0 : undefined}
              onClick={
                active.canTrade
                  ? undefined
                  : () =>
                      trackSto('sto_trade_cta_clicked', {
                        venue: active.source,
                        locked: true,
                        reason: active.lockedReasonKey?.split('.').pop(),
                      })
              }>
              <TradeAction
                type="button"
                black
                disabled={!active.canTrade}
                aria-disabled={!active.canTrade}
                aria-haspopup={active.source === 'bitfinex' ? 'dialog' : undefined}
                data-cy={`sto-trade-cta-${active.source}`}
                onClick={() => {
                  if (!active.canTrade) return
                  trackSto('sto_trade_cta_clicked', { venue: active.source, locked: false })
                  if (active.source === 'bitfinex') setRiskModalOpen(true)
                  else window.open(active.url, '_blank', 'noopener,noreferrer')
                }}>
                <Copy id="stoPage.keyActions.trade.goTo" values={{ venue: active.name }} />
                <ExternalLinkIcon width={12} height={12} aria-hidden="true" />
              </TradeAction>
            </LockedTrigger>
          </Tooltip>
        )}

        <Disclaimer>
          <Copy id="stoPage.keyActions.trade.disclaimer" />
        </Disclaimer>

        {!active.isChecking && active.notice && <Notice {...active.notice} />}
      </VenueBody>

      <TradeRiskModal isOpen={riskModalOpen} venue={active.name} url={active.url} onClose={closeRiskModal} />
    </>
  )
}
